import { useApolloClient } from "@apollo/client";
import { LyfeTooltip, Question } from "@lyfegen-platform/react-common";
import { IconButton, Typography } from "@material-ui/core";
import { Block, Check, Clear } from "@material-ui/icons";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSetRecoilState } from "recoil";
import { useDataMutation } from "../../../../../hooks/useDataModule";
import { SnackBarMessageType } from "../../../../../models/snackbarMessageType";
import { snackbarState } from "../../../../../store/layout";
import { DocumentStatus, DocumentType, FinancialDataPointStatus } from "../../../../../models/generated/graphql";
import startUpper from "../../../../../utils/start-upper";
import { useDocumentContext } from "../DocumentContextProvider";
import { SAVE_REVIEWED_INVOICE } from "../queries";
import { IRoleInfoProps } from "../refunds/RefundsTable";
import FlexTypographyComponent from "apps/platform-client/client/src/components/FlexTypographyComponent";

export const acceptanceStatuses = [
	FinancialDataPointStatus.Approved,
	FinancialDataPointStatus.Undecided,
	FinancialDataPointStatus.Declined,
];
export const acceptanceStatusColors: Record<FinancialDataPointStatus, string> = {
	[FinancialDataPointStatus.Declined]: "#ed544d",
	[FinancialDataPointStatus.Undecided]: "#7ea6b4",
	[FinancialDataPointStatus.Approved]: "#038331",
	[FinancialDataPointStatus.Removed]: "#9e9e9e",
};

export type InvoiceAcceptanceStatus = FinancialDataPointStatus;

export const getIcon = (status: FinancialDataPointStatus, selected?: FinancialDataPointStatus) => {
	const color = selected === status ? "white" : "#cddcdc";
	switch (status) {
		case FinancialDataPointStatus.Declined:
			return <Clear style={{ fontSize: "1rem", color }} />;
		case FinancialDataPointStatus.Undecided:
			return <Question style={{ fontSize: "1rem", color }} />;
		case FinancialDataPointStatus.Approved:
			return <Check style={{ fontSize: "1rem", color }} />;
		case FinancialDataPointStatus.Removed:
			return <Block style={{ fontSize: "1rem", color }} />;
	}
};

interface AcceptanceStatusProps {
	id: string;
	invoiceId: string;
	amount: number;
	adjustedAmount?: number | null;
	documentType: DocumentType;
	canUpdate: boolean;
	isSentForExternalReview?: boolean;
	areActionsBlockedBySmartMipStatus: boolean;
	isAcceptanceDisabledForExternalParty?: boolean;
	roleInfo?: IRoleInfoProps;
	isExposure?: boolean;
	areDecisionsLocked?: boolean;
	showLoader?: (isSaving: boolean) => void;
	reviewingParty?: string;
}

const AcceptanceStatus = (props: AcceptanceStatusProps) => {
	const [save] = useDataMutation(SAVE_REVIEWED_INVOICE);
	const { t } = useTranslation();
	const { currentState, onStateChange, refetch } = useDocumentContext();
	const setSnackBar = useSetRecoilState(snackbarState);
	const apolloClient = useApolloClient();
	const { canUpdate, documentType, id, invoiceId, amount, roleInfo, adjustedAmount } = props;
	const selected = useMemo(
		() => currentState.find((refund) => refund.fdpUuid === id)?.status ?? FinancialDataPointStatus.Undecided,
		[currentState, id]
	);

	const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, status: InvoiceAcceptanceStatus) => {
		if (selected === status || props.isSentForExternalReview || !currentState.length) return;

		const updatedFinancialDataPoints = currentState.map((fdp) => {
			if (fdp.fdpUuid === id) {
				return {
					generatedUuid: id,
					amount: amount,
					status: status,
					adjustedAmount: adjustedAmount ? adjustedAmount : null,
				};
			}

			return {
				generatedUuid: fdp.fdpUuid,
				amount: fdp.amount,
				status: fdp.status,
				adjustedAmount: fdp.adjustedAmount ? fdp.adjustedAmount : null,
			};
		});

		const input = {
			invoiceId,
			invoiceStatus: DocumentStatus.InReview,
			documentType,
			isExposure: !!props.isExposure,
			financialDataPoints: [...updatedFinancialDataPoints],
			isApproved: false,
			reviewerOrganization: props.reviewingParty,
		};
		onStateChange(
			updatedFinancialDataPoints.map(({ generatedUuid, status }) => ({
				fdpUuid: generatedUuid,
				status,
			}))
		);
		props.showLoader && props.showLoader(true);
		save({
			variables: { input },
			onCompleted: () => {
				props.showLoader && props.showLoader(false);
				refetch();
			},
			onError: (error) => {
				props.showLoader && props.showLoader(false);
				onStateChange(
					currentState.map(({ fdpUuid, status }) => ({
						fdpUuid,
						status,
					}))
				);
				setSnackBar({
					isOpen: true,
					message: error.message,
					type: SnackBarMessageType.Error,
					duration: 8000,
				});
				apolloClient.refetchQueries({ include: "active" });
			},
		});
	};

	if (selected === FinancialDataPointStatus.Removed) {
		return null;
	}

	return (
		<div style={{ width: "100%", display: "flex", justifyContent: "left" }}>
			{roleInfo?.isReviewer && !roleInfo?.isWaitingForApproval && !props.areActionsBlockedBySmartMipStatus && canUpdate && (
				<div style={{ justifyContent: "right" }}>
					{acceptanceStatuses.map((s, i) => (
						<LyfeTooltip title={props.areDecisionsLocked ? t("document-decision-disabled") : ""}>
							<span>
								<IconButton
									key={id + i}
									style={{
										margin: "0px 16px 0px 0px",
										backgroundColor: selected === s ? acceptanceStatusColors[s] : "",
										border: `1px solid #cddcdc`,
									}}
									onClick={(e) => handleClick(e, s)}
									disabled={
										props.isAcceptanceDisabledForExternalParty ||
										props.isSentForExternalReview ||
										props.areDecisionsLocked
									}
									size="small"
								>
									{getIcon(s, selected)}
								</IconButton>
							</span>
						</LyfeTooltip>
					))}
				</div>
			)}
			{(roleInfo?.isCreator || !canUpdate) && !props.isExposure && (
				<IconButton
					style={{
						margin: "0px 16px 0px 0px",
						backgroundColor: acceptanceStatusColors[selected],
						border: `1px solid #cddcdc`,
						cursor: "auto",
					}}
					disableRipple
					size="small"
				>
					{getIcon(selected, selected)}
				</IconButton>
			)}
			<FlexTypographyComponent
				variant="body2"
				text={startUpper(t(selected || ""))}
				typographyProps={{ style: { color: acceptanceStatusColors[selected] } }}
			/>
		</div>
	);
};

export default AcceptanceStatus;
